import { useState, type FC } from "react";
import { CalendarDays, Mail, MessagesSquare, Phone, X } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import { useIntercom } from "@/shared/providers/IntercomProvider";
import { CALENDLY_URLS } from "@/shared/constants/calendlyUrls";
import { SUPPORT_PHONE } from "@/shared/constants/constants";
import { IconButton } from "@/shared/components/ui/icon-button";

interface SupportPanelProps {
  children: React.ReactNode;
}

export const SupportPanel: FC<SupportPanelProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const { openSidePanel } = useIntercom();

  const handleChat = () => {
    setOpen(false);
    openSidePanel();
  };

  const handleSchedule = () => {
    setOpen(false);
    setCalendlyOpen(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[340px] p-6 shadow-[0_0_12px_rgba(0,0,0,0.09)]"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between">
                <p className="flex-1 text-base font-medium text-primary">
                  Get support from the Elayne team
                </p>
                <IconButton onClick={() => setOpen(false)} label="Close support" tooltip={false}>
                  <X />
                </IconButton>
              </div>
              <p className="text-sm text-secondary">
                Help with onboarding, client invites, estate setup, and product questions.
              </p>
            </div>

            <div className="border-t border-primary" />

            <div className="flex flex-col gap-6">
              <button
                onClick={handleChat}
                className="flex items-center gap-3 text-left transition-colors hover:opacity-80"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-blue-100">
                  <MessagesSquare className="h-4 w-4 text-blue-600" />
                </span>
                <span className="text-sm font-medium text-primary">Chat with the Elayne team</span>
              </button>

              <button
                onClick={handleSchedule}
                className="flex items-center gap-3 text-left transition-colors hover:opacity-80"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-blue-100">
                  <CalendarDays className="h-4 w-4 text-blue-600" />
                </span>
                <span className="text-sm font-medium text-primary">
                  Schedule an onboarding call
                </span>
              </button>
            </div>

            <div className="border-t border-primary" />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-secondary" />
                <span className="text-sm font-medium text-secondary">support@elayne.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-secondary" />
                <span className="text-sm font-medium text-secondary">{SUPPORT_PHONE.display}</span>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <CalendlyModal
        isOpen={calendlyOpen}
        onClose={() => setCalendlyOpen(false)}
        url={CALENDLY_URLS.ATTORNEY_ONBOARDING}
      />
    </>
  );
};
